import { useState } from 'react'
import { type User } from '@budgetmonkey/shared'
import { Button, Card, ErrorNote, Field, Input } from '@/components/ui'
import { useUpdateUserProfile } from '@/features/user/hooks'

/**
 * The two lines that print at the top of every log sheet.
 *
 * They were captured once, in the first-run wizard, and there was no way back
 * to them — a department that changed its name, or a typo caught on a printed
 * packet, was uncorrectable from inside the app. That is the gap the Settings
 * screen's own comment predicted, and it surfaced exactly as predicted: a
 * packet printed "Innovation" for a department now called something longer.
 *
 * A form with Save rather than save-on-blur: both fields go on a document
 * handed to the business office, and a half-typed department is not something
 * to commit on the way past.
 */
export function PacketHeaderCard({ user }: { user: User }) {
  const update = useUpdateUserProfile()
  const [cardholderName, setCardholderName] = useState(user.cardholderName)
  const [department, setDepartment] = useState(user.department)
  const [saved, setSaved] = useState(false)
  // Both fields are empty on an account that never finished setup, and an
  // untouched form has no business telling someone off for that. Same gate the
  // budget-code forms use.
  const [touched, setTouched] = useState(false)

  const trimmedName = cardholderName.trim()
  const trimmedDepartment = department.trim()
  const changed =
    trimmedName !== user.cardholderName || trimmedDepartment !== user.department
  const canSave = trimmedName !== '' && trimmedDepartment !== '' && changed

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!canSave) return
    update.mutate(
      { cardholderName: trimmedName, department: trimmedDepartment },
      {
        onSuccess: () => {
          setSaved(true)
          setTimeout(() => setSaved(false), 3000)
        },
      },
    )
  }

  return (
    <Card className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Packet header</h2>
        <p className="mt-1 text-sm text-slate-600">
          Printed at the top of every log sheet, exactly as written here.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        {update.error !== null && <ErrorNote>{update.error.message}</ErrorNote>}

        <div className="grid gap-5 sm:grid-cols-2">
          <Field
            label="Cardholder's name"
            required
            error={
              touched && trimmedName === ''
                ? 'Required — it appears on every packet.'
                : undefined
            }
          >
            <Input
              value={cardholderName}
              onChange={(e) => setCardholderName(e.target.value)}
              onBlur={() => setTouched(true)}
              invalid={touched && trimmedName === ''}
              placeholder="Matthew Callison"
              maxLength={120}
            />
          </Field>

          <Field
            label="Department / Building"
            required
            error={
              touched && trimmedDepartment === ''
                ? 'Required — it appears on every packet.'
                : undefined
            }
          >
            <Input
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              onBlur={() => setTouched(true)}
              invalid={touched && trimmedDepartment === ''}
              placeholder="Innovation and Strategic Partnerships"
              maxLength={160}
            />
          </Field>
        </div>

        <div className="flex items-center gap-3">
          <Button type="submit" variant="primary" disabled={!canSave || update.isPending}>
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
          {/* Only ever shown after a write actually succeeded. */}
          {saved && !changed && (
            <span className="text-sm font-medium text-emerald-700" role="status">
              Saved. Packets generated from now on will use it.
            </span>
          )}
        </div>
      </form>
    </Card>
  )
}
